<template>
    <div class="recurring-scheduled-services">
        <div v-if="loading" class="text-center py-3">
            <span class="spinner-border spinner-border-sm" role="status"></span>
            Loading...
        </div>
        <div v-else-if="services.length > 0" class="table-responsive">
            <table class="table table-bordered mb-0">
                <thead class="bg-primary text-white">
                    <tr>
                        <th v-for="service in services" :key="service.id" class="text-center">
                            {{ service.service_name }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td v-for="service in services" :key="service.id" class="text-center">
                            <div class="d-flex align-items-center justify-content-center">
                                <VueDatePicker
                                    v-model="service.next_due_date"
                                    :enable-time-picker="false"
                                    format="dd-MM-yyyy"
                                    model-type="yyyy-MM-dd"
                                    auto-apply
                                    :clearable="false"
                                    :disabled="!service.is_enabled"
                                    placeholder="Select Date"
                                    input-class-name="form-control form-control-sm text-center"
                                    :teleport="true"
                                    @update:model-value="updateDate(service)"
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-else class="text-muted text-center py-3">
            No recurring services found.
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
    name: 'RecurringScheduledServices',
    props: {
        addressId: {
            type: [Number, String],
            required: true
        },
        customerId: {
            type: [Number, String],
            required: true
        }
    },
    data() {
        return {
            services: [],
            loading: false
        };
    },
    computed: {
        hasSelectedServices() {
            return this.services.some(service => !!service.next_due_date);
        }
    },
    mounted() {
        this.fetchRecurringServices();
    },
    watch: {
        addressId: {
            handler() {
                this.fetchRecurringServices();
            }
        },
        customerId: {
            handler() {
                this.fetchRecurringServices();
            }
        }
    },
    methods: {
        async fetchRecurringServices() {
            if (!this.addressId || !this.customerId) {
                this.services = [];
                return;
            }

            this.loading = true;
            try {
                const response = await axios.post('/api/admin/recurring-services/list', {
                    address_id: this.addressId,
                    customer_id: this.customerId
                });

                if (response.data.status === 'success') {
                    this.services = response.data.services || [];
                } else {
                    this.services = [];
                }
            } catch (error) {
                console.error('Error fetching recurring services:', error);
                this.services = [];
            } finally {
                this.loading = false;
                this.$emit('services-loaded', this.services);
            }
        },
        updateDate(service) {
            // If date is cleared, we assume we want to disable/unset the service
            
            this.loading = true;
            axios.post(`/admin/users/scheduled-service-update/${this.customerId}`, {
                id: service.setting_id || 0, // Pass existing setting ID if any
                service_id: service.id,
                address_id: this.addressId,
                customer_id: this.customerId,
                due_date: service.next_due_date,
                enabled: !!service.next_due_date // Enable if date is set, disable if cleared
            })
            .then(response => {
                if (response.data.status === 'success') {
                     this.$notification('success', 'Service date updated successfully');
                     this.fetchRecurringServices(); // Refresh to get precise state
                } else {
                     this.$notification('error', response.data.message || 'Error updating service');
                }
            })
            .catch(error => {
                console.error('Error updating service:', error);
                this.$notification('error', 'Failed to update service date');
            })
            .finally(() => {
                this.loading = false;
            });
        }
    }
};
</script>

<style scoped>
.recurring-scheduled-services .table thead th {
    font-weight: 600;
    font-size: 0.875rem;
    white-space: nowrap;
}

.recurring-scheduled-services .table tbody td {
    vertical-align: middle;
}

.recurring-scheduled-services .form-control {
    border: 1px solid #dee2e6;
    background-color: #fff;
}

.recurring-scheduled-services .btn-link {
    color: #6c757d;
}

.recurring-scheduled-services .btn-link:hover {
    color: #0d6efd;
}
</style>
